import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  listDefinitions,
  activateDefinition,
  deprecateDefinition,
  deactivateDefinition,
  deleteDefinition,
  cloneDefinition,
  WORKFLOW_STATUSES,
  type WorkflowDefinition,
} from '../../api/workflowApi.js';
import { sx, StatusBadge } from '../workflow/wfKit.js';

/**
 * WorkflowDesignerPage — the M04 authoring hub (UI Audit M04 C1).
 *
 * Replaces the former local-state-only stub (no API, no persistence) with a
 * real designer home backed by `/v1/wf/*`: it lists the tenant's workflow
 * definitions and drives every authoring lifecycle action —
 * create → design steps → validate → activate → deprecate → clone → delete —
 * against the backend. Per-definition deep authoring (steps, conditions,
 * approver rules, TAT/escalation) lives on the Steps Designer, reached via the
 * "Design" action.
 */
export function WorkflowDesignerPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<WorkflowDefinition[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [cloneId, setCloneId] = useState<string | null>(null);
  const [cloneCode, setCloneCode] = useState('');

  const load = useCallback(() => {
    setRows(null);
    listDefinitions(token, status ? { status } : {})
      .then((r) => { setRows(r.rows); setError(null); })
      .catch((e) => setError((e as Error).message));
  }, [token, status]);

  useEffect(() => { load(); }, [load]);

  async function doActivate(id: string): Promise<void> {
    setBusyId(id); setError(null); setNotice(null);
    try {
      const res = await activateDefinition(token, id);
      if (res && typeof res === 'object' && 'errors' in res && Array.isArray(res.errors)) {
        setError(t('approvals.wf.err.activateFailed', { errors: res.errors.join('; ') }));
      } else {
        setNotice(t('approvals.wf.activated'));
        load();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function doDeprecate(id: string): Promise<void> {
    setBusyId(id); setError(null); setNotice(null);
    try {
      await deprecateDefinition(token, id);
      setNotice(t('approvals.wf.deprecated'));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function doDeactivate(id: string): Promise<void> {
    setBusyId(id); setError(null); setNotice(null);
    try {
      await deactivateDefinition(token, id);
      setNotice(t('approvals.wf.deactivated'));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function doDelete(id: string): Promise<void> {
    setBusyId(id); setError(null); setNotice(null);
    try {
      await deleteDefinition(token, id);
      setConfirmDeleteId(null);
      setNotice(t('approvals.wf.deleted'));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function doClone(id: string): Promise<void> {
    if (!cloneCode.trim()) { setError(t('approvals.wf.err.cloneCode')); return; }
    setBusyId(id); setError(null); setNotice(null);
    try {
      await cloneDefinition(token, id, { newWorkflowCode: cloneCode.trim() });
      setCloneId(null); setCloneCode('');
      setNotice(t('approvals.wf.cloned'));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main aria-label="workflow-designer" style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{t('approvals.wf.title')}</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/admin/wf/templates" style={sx.secondaryBtn}>{t('approvals.wf.templates')}</Link>
          <Link to="/admin/wf/dashboard" style={sx.secondaryBtn}>{t('approvals.wf.dashboard')}</Link>
          <Link to="/admin/wf/instances" style={sx.secondaryBtn}>{t('approvals.wf.instances')}</Link>
          <Link to="/admin/wf/definitions/new" style={sx.primaryBtn}>{t('approvals.wf.newDefinition')}</Link>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label htmlFor="wf-status" style={{ marginRight: 8, fontSize: 13 }}>{t('approvals.wf.status')}</label>
        <select
          id="wf-status" aria-label="status-filter" value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}
        >
          <option value="">{t('approvals.wf.all')}</option>
          {WORKFLOW_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      {!rows ? (
        <p>{t('approvals.wf.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="designer-empty" style={{ color: 'var(--color-text-muted)' }}>
          {t('approvals.wf.empty')}
        </p>
      ) : (
        <table aria-label="designer-table" style={sx.table}>
          <thead>
            <tr style={sx.theadTr}>
              <th style={sx.th}>{t('approvals.wf.col.code')}</th>
              <th style={sx.th}>{t('approvals.wf.col.name')}</th>
              <th style={sx.th}>{t('approvals.wf.col.entity')}</th>
              <th style={sx.th}>{t('approvals.wf.col.status')}</th>
              <th style={sx.th}>{t('approvals.wf.col.ver')}</th>
              <th style={sx.thRight}>{t('approvals.wf.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const isDraft = d.workflowStatus === 'DRAFT';
              const isActive = d.workflowStatus === 'ACTIVE';
              // A chain that was deprecated could never be brought back from
              // this screen: Activate was offered only on a DRAFT, so a tenant
              // that retired a chain had no way to resume it and the process
              // it served simply stopped resolving to a workflow. The server
              // has no such rule — it validates the structure and activates —
              // so anything not already running may be activated here.
              const canActivate = !isActive;
              const rowBusy = busyId === d.workflowDefinitionId;
              return (
                <tr key={d.workflowDefinitionId} data-testid={`designer-row-${d.workflowDefinitionId}`} style={sx.tbodyTr}>
                  <td style={sx.td}>{d.workflowCode}</td>
                  <td style={sx.td}>{d.workflowName}</td>
                  <td style={sx.td}>{d.entityType}</td>
                  <td style={sx.td}><StatusBadge status={d.workflowStatus} /></td>
                  <td style={sx.td}>{d.versionNumber}</td>
                  <td style={sx.tdRight}>
                    {cloneId === d.workflowDefinitionId ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <input
                          aria-label={`clone-code-${d.workflowDefinitionId}`}
                          value={cloneCode}
                          onChange={(e) => setCloneCode(e.target.value)}
                          placeholder={t('approvals.wf.newCode')}
                          style={{ padding: 4, border: '1px solid var(--color-border-default)', borderRadius: 4 }}
                        />
                        <button type="button" style={sx.primaryBtn} disabled={rowBusy}
                          aria-label={`clone-confirm-${d.workflowDefinitionId}`}
                          onClick={() => doClone(d.workflowDefinitionId)}>{t('approvals.wf.save')}</button>
                        <button type="button" style={sx.secondaryBtn}
                          onClick={() => { setCloneId(null); setCloneCode(''); }}>{t('approvals.wf.cancel')}</button>
                      </span>
                    ) : confirmDeleteId === d.workflowDefinitionId ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 12 }}>{t('approvals.wf.deletePrompt')}</span>
                        <button type="button" style={sx.dangerBtn} disabled={rowBusy}
                          aria-label={`delete-confirm-${d.workflowDefinitionId}`}
                          onClick={() => doDelete(d.workflowDefinitionId)}>{t('approvals.wf.yes')}</button>
                        <button type="button" style={sx.secondaryBtn}
                          onClick={() => setConfirmDeleteId(null)}>{t('approvals.wf.no')}</button>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Link to={`/admin/wf/definitions/${d.workflowDefinitionId}/design`} style={sx.secondaryBtn}>{t('approvals.wf.design')}</Link>
                        <Link to={`/admin/wf/definitions/${d.workflowDefinitionId}/simulate`} style={sx.secondaryBtn}>{t('approvals.wf.simulate')}</Link>
                        {isDraft && (
                          <Link to={`/admin/wf/definitions/${d.workflowDefinitionId}/edit`} style={sx.secondaryBtn}>{t('approvals.wf.edit')}</Link>
                        )}
                        {canActivate && (
                          <button type="button" style={sx.primaryBtn} disabled={rowBusy}
                            aria-label={`activate-${d.workflowDefinitionId}`}
                            onClick={() => doActivate(d.workflowDefinitionId)}>{t('approvals.wf.activate')}</button>
                        )}
                        {isActive && (
                          <button type="button" style={sx.secondaryBtn} disabled={rowBusy}
                            aria-label={`deactivate-${d.workflowDefinitionId}`}
                            onClick={() => doDeactivate(d.workflowDefinitionId)}>{t('approvals.wf.deactivate')}</button>
                        )}
                        {isActive && (
                          <button type="button" style={sx.secondaryBtn} disabled={rowBusy}
                            aria-label={`deprecate-${d.workflowDefinitionId}`}
                            onClick={() => doDeprecate(d.workflowDefinitionId)}>{t('approvals.wf.deprecate')}</button>
                        )}
                        <button type="button" style={sx.secondaryBtn} disabled={rowBusy}
                          aria-label={`clone-${d.workflowDefinitionId}`}
                          onClick={() => { setCloneId(d.workflowDefinitionId); setCloneCode(''); }}>{t('approvals.wf.clone')}</button>
                        {isDraft && (
                          <button type="button" style={sx.dangerBtn} disabled={rowBusy}
                            aria-label={`delete-${d.workflowDefinitionId}`}
                            onClick={() => setConfirmDeleteId(d.workflowDefinitionId)}>{t('approvals.wf.delete')}</button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
